import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Check, Video, Pencil } from "lucide-react";
import {
    Modal, TextInput, SelectInput, ChipMultiSelect, PrimaryBtn, GhostBtn, Tag,
} from "./CrmUI";
import { RichTextEditor } from "./RichTextEditor";
import {
    createLead, updateLead, createClient, updateClient,
    getNotes, createNote, updateNote, completeNote, deleteNote,
    sendCalendarInvite, getCalendarEvents, calendarStatus, toTitleCase,
    workTagClasses, WORK_TAG_PALETTE,
} from "./crmApi";

const SCORE_OPTIONS = ["0", "1", "2", "3", "4", "5", "Converted", "Closed"];

// Each modal is mounted/unmounted (and re-keyed) by its parent, so local state
// is initialised once from props — no reset-on-prop-change effects needed.

// ── Add / Edit Lead ──────────────────────────────────────────────────────────
export const LeadModal = ({ onClose, lead, config, onSaved }) => {
    const [form, setForm] = useState(() => lead
        ? { ...lead }
        : { name: "", email: "", phone: "", source: "", company: "", isb_batch: "", tags: [], lead_score: "0", next_step: "", custom: {} });
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(lead?.id);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setCustom = (k, v) => setForm((f) => ({ ...f, custom: { ...(f.custom || {}), [k]: v } }));

    const save = async () => {
        if (!form.name?.trim()) { toast.error("Name is required."); return; }
        setSaving(true);
        try {
            const payload = {
                name: form.name, email: form.email || "", phone: form.phone || "",
                source: form.source || "", company: form.company || "", isb_batch: form.isb_batch || "",
                tags: form.tags || [], lead_score: form.lead_score || "0", next_step: form.next_step || "",
                message: form.message || "", custom: form.custom || {},
            };
            if (isEdit) await updateLead(lead.id, payload); else await createLead(payload);
            toast.success(isEdit ? "Lead updated." : "Lead added.");
            onSaved(); onClose();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not save lead."); }
        finally { setSaving(false); }
    };

    return (
        <Modal open onClose={onClose} title={isEdit ? "Edit lead" : "Add lead"} maxWidth="max-w-2xl" testId="lead-modal">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextInput label="Name *" value={form.name} onChange={(v) => set("name", v)} testId="lead-form-name" />
                <TextInput label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} testId="lead-form-email" />
                <TextInput label="Mobile" value={form.phone} onChange={(v) => set("phone", v)} testId="lead-form-phone" />
                <TextInput label="Company" value={form.company} onChange={(v) => set("company", v)} testId="lead-form-company" />
                <SelectInput label="Source" value={form.source} onChange={(v) => set("source", v)} options={["", ...(config?.sources || [])]} testId="lead-form-source" />
                <TextInput label="ISB / Batch" value={form.isb_batch} onChange={(v) => set("isb_batch", v)} testId="lead-form-isb" />
                <SelectInput label="Lead score" value={form.lead_score} onChange={(v) => set("lead_score", v)} options={SCORE_OPTIONS} testId="lead-form-score" />
                <SelectInput label="Next step" value={form.next_step} onChange={(v) => set("next_step", v)} options={["", ...(config?.next_steps || [])]} testId="lead-form-nextstep" />
            </div>
            <div className="mt-4">
                <ChipMultiSelect label="Tags" options={config?.tags || []} selected={form.tags} onChange={(v) => set("tags", v)} testId="lead-form-tags" />
            </div>
            {(config?.lead_columns || []).length > 0 && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {config.lead_columns.map((col) => (
                        <TextInput key={col.key} label={col.label} type={col.type === "number" ? "number" : col.type === "date" ? "date" : "text"}
                            value={form.custom?.[col.key]} onChange={(v) => setCustom(col.key, v)} testId={`lead-form-custom-${col.key}`} />
                    ))}
                </div>
            )}
            <div className="flex items-center justify-end gap-3 mt-6">
                <GhostBtn onClick={onClose} testId="lead-form-cancel">Cancel</GhostBtn>
                <PrimaryBtn onClick={save} disabled={saving} testId="lead-form-save">{saving ? "Saving…" : "Save lead"}</PrimaryBtn>
            </div>
        </Modal>
    );
};

// ── Historical Notes (+ Work tasks for clients) ─────────────────────────────
export const NotesModal = ({ onClose, entity, entityType, allowWork = false, config, onConfigChange }) => {
    const [notes, setNotes] = useState(null);
    const [kind, setKind] = useState("note");
    const [subject, setSubject] = useState("");
    const [noteDate, setNoteDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [body, setBody] = useState("");
    const [tag, setTag] = useState("");
    const [tagDraft, setTagDraft] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);

    const workTags = config?.work_tags || [];
    const tagColorOf = (label) => workTags.find((t) => t.label === label)?.color;

    const reload = async () => {
        try { setNotes(await getNotes(entityType, entity.id)); } catch { setNotes([]); }
    };

    useEffect(() => {
        let active = true;
        (async () => {
            try { const n = await getNotes(entityType, entity.id); if (active) setNotes(n); }
            catch { if (active) setNotes([]); }
        })();
        return () => { active = false; };
    }, [entityType, entity.id]);

    const resetForm = () => {
        setEditingId(null); setKind("note"); setSubject("");
        setNoteDate(new Date().toISOString().slice(0, 10));
        setBody(""); setTag(""); setTagDraft("");
    };

    const startEdit = (n) => {
        setEditingId(n.id);
        setKind(n.kind || "note");
        setSubject(n.subject || "");
        setNoteDate((n.note_date || n.created_at || "").slice(0, 10) || new Date().toISOString().slice(0, 10));
        setBody(n.body_html || "");
        setTag(n.tag || ""); setTagDraft("");
    };

    const add = async () => {
        if (!subject.trim()) { toast.error("Subject is required."); return; }
        const isWork = allowWork && kind === "work";
        // Prefer a freshly typed custom tag over the selected preset, then fall
        // back to the preset — so typing always wins without an extra Enter/deselect.
        const usedTag = isWork ? (tagDraft.trim() || tag || "").trim() : "";
        setSaving(true);
        try {
            if (editingId) {
                await updateNote(editingId, { subject, note_date: noteDate, body_html: body, tag: usedTag });
            } else {
                await createNote({ entity_type: entityType, entity_id: entity.id, subject, note_date: noteDate, body_html: body, kind: isWork ? "work" : "note", tag: usedTag });
            }
            const wasCustom = usedTag && !workTags.some((t) => t.label.toLowerCase() === usedTag.toLowerCase());
            resetForm();
            toast.success(editingId ? "Changes saved." : (isWork ? "Work item added." : "Note added."));
            reload();
            if (wasCustom && onConfigChange) onConfigChange();
        } catch { toast.error("Could not save note."); }
        finally { setSaving(false); }
    };

    const markDone = async (id) => {
        try { await completeNote(id); toast.success("Work marked completed."); reload(); }
        catch { toast.error("Could not update."); }
    };
    const remove = async (id) => { try { await deleteNote(id); reload(); } catch { toast.error("Could not delete."); } };

    const list = notes || [];
    const pendingWork = allowWork ? list.filter((n) => n.kind === "work" && !n.completed) : [];
    const timeline = list.filter((n) => !(n.kind === "work" && !n.completed));

    return (
        <Modal open onClose={onClose} title={`Notes — ${entity?.name || ""}`} subtitle={allowWork ? "Work tasks & historical notes" : "Historical notes & talk history"} maxWidth="max-w-2xl" testId="notes-modal">
            <div className="rounded-2xl border border-oth-line/70 bg-white p-4 mb-5">
                {allowWork && !editingId && (
                    <div className="flex items-center gap-2 mb-3" data-testid="note-kind-toggle">
                        <button type="button" onClick={() => setKind("note")} className={`px-3.5 py-1.5 rounded-full text-xs font-body border transition-all ${kind === "note" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-oth-ink2 border-oth-line/70 hover:border-oth-ink"}`} data-testid="note-kind-note">Note</button>
                        <button type="button" onClick={() => setKind("work")} className={`px-3.5 py-1.5 rounded-full text-xs font-body border transition-all ${kind === "work" ? "bg-[#B5651D] text-white border-[#B5651D]" : "bg-white text-oth-ink2 border-oth-line/70 hover:border-oth-ink"}`} data-testid="note-kind-work">Work</button>
                        {kind === "work" && <span className="text-[11px] font-body text-oth-ink3">Pinned to top until marked completed.</span>}
                    </div>
                )}
                {editingId && (
                    <div className="flex items-center justify-between gap-2 mb-3 rounded-xl bg-[#EFEFEA] px-3 py-2" data-testid="note-editing-banner">
                        <span className="text-[12px] font-body text-oth-ink2">Editing {kind === "work" ? "work item" : "note"}</span>
                        <button type="button" onClick={resetForm} className="text-[12px] font-body text-oth-ink3 hover:text-oth-ink" data-testid="note-cancel-edit">Cancel</button>
                    </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                    <div className="sm:col-span-2"><TextInput label="Subject" value={subject} onChange={setSubject} placeholder={kind === "work" ? "e.g. Collect KYC documents" : "e.g. Intro call"} testId="note-subject" /></div>
                    <TextInput label="Date" type="date" value={noteDate} onChange={setNoteDate} testId="note-date" />
                </div>
                {allowWork && kind === "work" && (
                    <div className="mb-3" data-testid="work-tag-selector">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-1.5">Tag <span className="normal-case tracking-normal text-oth-ink3/70">(one per work item)</span></div>
                        <div className="flex flex-wrap gap-2 mb-2">
                            {workTags.map((t) => {
                                const sel = tag === t.label;
                                return (
                                    <button type="button" key={t.label} onClick={() => setTag(sel ? "" : t.label)} className={`px-3 py-1 rounded-full text-xs font-body border transition-all ${workTagClasses(t.color)} ${sel ? "ring-2 ring-offset-1 ring-[#0A0A0A]" : "opacity-75 hover:opacity-100"}`} data-testid={`work-tag-opt-${t.label}`}>{t.label}</button>
                                );
                            })}
                        </div>
                        <input value={tagDraft} onChange={(e) => setTagDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && tagDraft.trim()) { e.preventDefault(); setTag(tagDraft.trim()); setTagDraft(""); } }} placeholder="Or type a custom tag and press Enter" className="w-full bg-white border border-oth-line/70 rounded-xl px-3 py-2 font-body text-sm focus:outline-none focus:border-oth-blue" data-testid="work-tag-custom" />
                        {tag && !workTags.some((t) => t.label === tag) && (
                            <div className="mt-1.5 text-[12px] font-body text-oth-ink2" data-testid="work-tag-custom-note">Custom tag: <span className="font-medium">{tag}</span> <span className="text-oth-ink3">— will be saved to settings.</span></div>
                        )}
                    </div>
                )}
                <div className="text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-1.5">{kind === "work" ? "Task details" : "Notes"}</div>
                <RichTextEditor value={body} onChange={setBody} testId="note-body" />
                <div className="flex justify-end gap-2 mt-3">
                    {editingId && <GhostBtn onClick={resetForm} testId="note-edit-cancel">Cancel</GhostBtn>}
                    <PrimaryBtn onClick={add} disabled={saving} testId="note-add">
                        {editingId ? <><Check size={14} /> Save changes</> : <><Plus size={14} /> {kind === "work" && allowWork ? "Add work" : "Add note"}</>}
                    </PrimaryBtn>
                </div>
            </div>

            {/* Pinned pending work */}
            {pendingWork.length > 0 && (
                <div className="mb-5" data-testid="pending-work">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#B5651D] font-body mb-2">Pending work ({pendingWork.length})</div>
                    <div className="space-y-2">
                        {pendingWork.map((n) => (
                            <div key={n.id} className="rounded-2xl border border-[#E7C9A8] bg-[#FCF6EE] p-4" data-testid={`work-${n.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="font-display text-sm font-semibold text-oth-ink">{n.subject}</div>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className="text-[11px] text-oth-ink3 font-body">{(n.note_date || n.created_at || "").slice(0, 10)}</span>
                                            {n.tag && <span className={`px-2 py-0.5 rounded-full text-[10px] font-body border ${workTagClasses(tagColorOf(n.tag))}`} data-testid={`work-tag-chip-${n.id}`}>{n.tag}</span>}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button onClick={() => markDone(n.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0B6B3A] text-white text-[12px] font-body hover:opacity-90" data-testid={`work-complete-${n.id}`}><Check size={13} /> Mark completed</button>
                                        <button onClick={() => startEdit(n)} className="text-oth-ink3 hover:text-oth-blue w-7 h-7 flex items-center justify-center" data-testid={`work-edit-${n.id}`}><Pencil size={14} /></button>
                                        <button onClick={() => remove(n.id)} className="text-oth-ink3 hover:text-red-600 w-7 h-7 flex items-center justify-center" data-testid={`work-del-${n.id}`}><Trash2 size={14} /></button>
                                    </div>
                                </div>
                                {n.body_html && <div className="rt-render mt-2 font-body text-sm text-oth-ink2 leading-relaxed" dangerouslySetInnerHTML={{ __html: n.body_html }} />}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="space-y-3 max-h-[40vh] overflow-y-auto thin-scroll" data-testid="notes-list">
                {notes === null && <div className="text-sm italic text-oth-ink3 font-body">Loading…</div>}
                {notes && timeline.length === 0 && <div className="text-sm italic text-oth-ink3 font-body">No notes yet.</div>}
                {notes && timeline.map((n) => {
                    const isCompletedWork = n.kind === "work" && n.completed;
                    return (
                        <div key={n.id} className="rounded-2xl border border-oth-line/60 bg-white p-4" data-testid={`note-${n.id}`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="font-display text-sm font-semibold flex items-center gap-2 flex-wrap">
                                        {n.subject}
                                        {n.kind === "work" && n.tag && <span className={`px-2 py-0.5 rounded-full text-[10px] font-body border ${workTagClasses(tagColorOf(n.tag))}`} data-testid={`note-tag-chip-${n.id}`}>{n.tag}</span>}
                                        {isCompletedWork && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] text-[#0B6B3A] bg-[#0B6B3A]/10 px-2 py-0.5 rounded-full"><Check size={11} /> Work · Completed</span>}
                                    </div>
                                    <div className="text-[11px] text-oth-ink3 font-body mt-0.5">{(n.note_date || n.created_at || "").slice(0, 10)}</div>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <button onClick={() => startEdit(n)} className="text-oth-ink3 hover:text-oth-blue" data-testid={`note-edit-${n.id}`}><Pencil size={14} /></button>
                                    <button onClick={() => remove(n.id)} className="text-oth-ink3 hover:text-red-600" data-testid={`note-del-${n.id}`}><Trash2 size={14} /></button>
                                </div>
                            </div>
                            {n.body_html && <div className="rt-render mt-2 font-body text-sm text-oth-ink2 leading-relaxed" dangerouslySetInnerHTML={{ __html: n.body_html }} />}
                        </div>
                    );
                })}
            </div>
        </Modal>
    );
};

// ── Schedule Google Calendar invite (Accept / Decline) ──────────────────────
const RSVP_LABEL = {
    accepted: { t: "Accepted", c: "text-[#0B6B3A] bg-[#0B6B3A]/10" },
    declined: { t: "Declined", c: "text-red-600 bg-red-50" },
    tentative: { t: "Maybe", c: "text-[#B5651D] bg-[#B5651D]/10" },
    needsAction: { t: "No response", c: "text-oth-ink3 bg-[#EFEFEA]" },
    cancelled: { t: "Cancelled", c: "text-oth-ink3 bg-[#EFEFEA]" },
    unknown: { t: "—", c: "text-oth-ink3 bg-[#EFEFEA]" },
};

export const FollowupModal = ({ onClose, entity, entityType }) => {
    const [subject, setSubject] = useState(`Follow up with ${toTitleCase(entity?.name || "")}`.trim());
    const [when, setWhen] = useState("");
    const [duration, setDuration] = useState("30");
    const [notes, setNotes] = useState("");
    const [guests, setGuests] = useState([]);
    const [guestDraft, setGuestDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [status, setStatus] = useState(null);
    const [events, setEvents] = useState(null);

    const loadEvents = async () => {
        try { setEvents(await getCalendarEvents(entityType, entity.id)); } catch { setEvents([]); }
    };

    const addGuest = () => {
        const e = guestDraft.trim().toLowerCase();
        if (!e) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error("Enter a valid email address."); return; }
        if (e === (entity?.email || "").toLowerCase() || guests.includes(e)) { setGuestDraft(""); return; }
        setGuests((g) => [...g, e]); setGuestDraft("");
    };
    const removeGuest = (e) => setGuests((g) => g.filter((x) => x !== e));

    useEffect(() => {
        let active = true;
        (async () => {
            try { const s = await calendarStatus(); if (active) setStatus(s); } catch { if (active) setStatus({ connected: false }); }
            try { const e = await getCalendarEvents(entityType, entity.id); if (active) setEvents(e); } catch { if (active) setEvents([]); }
        })();
        return () => { active = false; };
    }, [entityType, entity.id]);

    const send = async () => {
        if (!entity?.email) { toast.error("This contact has no email."); return; }
        if (!when) { toast.error("Please choose a date & time."); return; }
        setSending(true);
        try {
            const res = await sendCalendarInvite({
                to_email: entity.email, to_name: entity.name, subject,
                when, duration_min: parseInt(duration, 10) || 30, notes,
                extra_emails: guests,
                entity_type: entityType, entity_id: entity.id,
            });
            const guestNote = guests.length ? ` (+${guests.length} guest${guests.length > 1 ? "s" : ""})` : "";
            toast.success(res?.meet_link
                ? `Invite sent with a Google Meet link${guestNote} — Google will email everyone to Accept/Decline.`
                : `Invite sent${guestNote} — Google will email everyone to Accept/Decline.`);
            setWhen(""); setNotes(""); setGuests([]); setGuestDraft("");
            loadEvents();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the invite."); }
        finally { setSending(false); }
    };

    const notConnected = status && !status.connected;

    return (
        <Modal open onClose={onClose} title="Schedule Google Calendar invite" subtitle={entity?.email || "No email on file"} maxWidth="max-w-xl" testId="followup-modal">
            {notConnected && (
                <div className="mb-4 text-[13px] font-body text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3.5 py-2.5" data-testid="followup-not-connected">
                    Google Calendar is not connected. Go to <span className="font-semibold">CMS → Integrations → Google Calendar</span> to connect, then schedule invites here.
                </div>
            )}
            <div className="space-y-4">
                <TextInput label="Subject" value={subject} onChange={setSubject} testId="followup-subject" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2"><TextInput label="Date & time (IST)" type="datetime-local" value={when} onChange={setWhen} hint="Created on the connected Google Calendar in Indian Standard Time." testId="followup-when" /></div>
                    <SelectInput label="Duration" value={duration} onChange={setDuration} options={[{ value: "15", label: "15 min" }, { value: "30", label: "30 min" }, { value: "45", label: "45 min" }, { value: "60", label: "60 min" }]} testId="followup-duration" />
                </div>
                <div className="flex items-center gap-1.5 text-[12px] font-body text-oth-ink3" data-testid="followup-meet-hint">
                    <Video size={13} className="text-oth-blue" /> A Google Meet link is added automatically and included in the invite email.
                </div>
                <div data-testid="followup-guests">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-1.5">Additional guests <span className="normal-case tracking-normal text-oth-ink3/70">(optional — also invited & emailed)</span></div>
                    {guests.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                            {guests.map((g) => (
                                <span key={g} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#EFEFEA] text-xs font-body text-oth-ink" data-testid={`followup-guest-${g}`}>
                                    {g}
                                    <button type="button" onClick={() => removeGuest(g)} className="text-oth-ink3 hover:text-red-600" data-testid={`followup-guest-del-${g}`}>×</button>
                                </span>
                            ))}
                        </div>
                    )}
                    <div className="flex gap-2">
                        <input type="email" value={guestDraft} onChange={(e) => setGuestDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addGuest(); } }} placeholder="name@example.com — press Enter to add" className="flex-1 bg-white border border-oth-line/70 rounded-xl px-3 py-2 font-body text-sm focus:outline-none focus:border-oth-blue" data-testid="followup-guest-input" />
                        <GhostBtn onClick={addGuest} testId="followup-guest-add">Add</GhostBtn>
                    </div>
                </div>
                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-1.5">Notes / message</div>
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full bg-white border border-oth-line/70 rounded-xl px-3.5 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue min-h-[80px]" data-testid="followup-notes" />
                </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
                <GhostBtn onClick={onClose} testId="followup-cancel">Close</GhostBtn>
                <PrimaryBtn onClick={send} disabled={sending || notConnected} testId="followup-send">{sending ? "Sending…" : "Send invite"}</PrimaryBtn>
            </div>

            {events && events.length > 0 && (
                <div className="mt-6 pt-5 border-t border-oth-line/60" data-testid="followup-events">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-2">Scheduled invites</div>
                    <div className="space-y-2 max-h-[28vh] overflow-y-auto thin-scroll">
                        {events.map((ev) => {
                            const r = RSVP_LABEL[ev.rsvp] || RSVP_LABEL.unknown;
                            return (
                                <div key={ev.id} className="flex items-center justify-between gap-3 rounded-xl border border-oth-line/60 bg-white px-3.5 py-2.5" data-testid={`followup-event-${ev.id}`}>
                                    <div className="min-w-0">
                                        <div className="font-body text-sm text-oth-ink truncate">{ev.summary}</div>
                                        <div className="text-[12px] text-oth-ink3 font-body">{String(ev.start).replace("T", " · ")} · {ev.duration_min} min</div>
                                        {(ev.extra_emails || []).length > 0 && <div className="text-[11px] text-oth-ink3 font-body" data-testid={`followup-guests-${ev.id}`}>+{ev.extra_emails.length} guest{ev.extra_emails.length > 1 ? "s" : ""}: {ev.extra_emails.join(", ")}</div>}
                                        {ev.meet_link && <a href={ev.meet_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] font-body text-oth-blue hover:underline mt-0.5" data-testid={`followup-meet-${ev.id}`}><Video size={12} /> Join Google Meet</a>}
                                    </div>
                                    <span className={`shrink-0 text-[11px] font-body px-2.5 py-1 rounded-full ${r.c}`} data-testid={`followup-rsvp-${ev.id}`}>{r.t}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </Modal>
    );
};

// ── Add / Edit Client ────────────────────────────────────────────────────────
export const ClientModal = ({ onClose, client, config, clients, onSaved }) => {
    const [form, setForm] = useState(() => client
        ? { ...client }
        : { name: "", email: "", phone: "", company: "", isb_batch: "", categories: [], will_created: false, birthday: "", family_id: "", family_role: "", tags: [] });
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(client?.id);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        if (!form.name?.trim()) { toast.error("Name is required."); return; }
        setSaving(true);
        try {
            const payload = {
                name: form.name, email: form.email || "", phone: form.phone || "", company: form.company || "",
                categories: form.categories || [], will_created: Boolean(form.will_created),
                birthday: form.birthday || "", family_id: form.family_id || "", family_role: form.family_role || "",
                tags: form.tags || [], source: form.source || "", isb_batch: form.isb_batch || "",
            };
            if (isEdit) await updateClient(client.id, payload); else await createClient(payload);
            toast.success(isEdit ? "Client updated." : "Client added.");
            onSaved(); onClose();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
        finally { setSaving(false); }
    };

    const familyOptions = [{ value: "", label: "— None —" }, ...(clients || []).filter((c) => c.id !== client?.id).map((c) => ({ value: c.id, label: c.name }))];

    return (
        <Modal open onClose={onClose} title={isEdit ? "Edit client" : "Add client"} maxWidth="max-w-2xl" testId="client-modal">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextInput label="Name *" value={form.name} onChange={(v) => set("name", v)} testId="client-form-name" />
                <TextInput label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} testId="client-form-email" />
                <TextInput label="Mobile" value={form.phone} onChange={(v) => set("phone", v)} testId="client-form-phone" />
                <TextInput label="Birthday" type="date" value={form.birthday} onChange={(v) => set("birthday", v)} testId="client-form-birthday" />
                <TextInput label="Company" value={form.company} onChange={(v) => set("company", v)} testId="client-form-company" />
                <TextInput label="ISB / Batch" value={form.isb_batch} onChange={(v) => set("isb_batch", v)} testId="client-form-isb" />
                <SelectInput label="Family member of" value={form.family_id} onChange={(v) => set("family_id", v)} options={familyOptions} testId="client-form-family" />
            </div>
            <div className="mt-4">
                <ChipMultiSelect label="Categories" options={config?.client_categories || []} selected={form.categories} onChange={(v) => set("categories", v)} testId="client-form-categories" />
            </div>
            <label className="flex items-center gap-3 mt-4 cursor-pointer">
                <input type="checkbox" checked={Boolean(form.will_created)} onChange={(e) => set("will_created", e.target.checked)} className="w-4 h-4 accent-[#0B3B60]" data-testid="client-form-will" />
                <span className="font-body text-sm text-oth-ink">Will created</span>
            </label>
            <div className="flex items-center justify-end gap-3 mt-6">
                <GhostBtn onClick={onClose} testId="client-form-cancel">Cancel</GhostBtn>
                <PrimaryBtn onClick={save} disabled={saving} testId="client-form-save">{saving ? "Saving…" : "Save client"}</PrimaryBtn>
            </div>
        </Modal>
    );
};

// ── Settings: manage option lists + custom columns ──────────────────────────
const ListEditor = ({ label, items, onChange, testId }) => {
    const [draft, setDraft] = useState("");
    return (
        <div data-testid={testId}>
            <div className="text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-2">{label}</div>
            <div className="flex flex-wrap gap-2 mb-2">
                {(items || []).map((it) => (
                    <span key={it} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#EFEFEA] text-xs font-body text-oth-ink">
                        {it}
                        <button onClick={() => onChange(items.filter((x) => x !== it))} className="text-oth-ink3 hover:text-red-600" data-testid={`${testId}-del-${it}`}>×</button>
                    </span>
                ))}
            </div>
            <div className="flex gap-2">
                <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && draft.trim()) { onChange([...(items || []), draft.trim()]); setDraft(""); } }}
                    placeholder="Type and press Enter" className="flex-1 bg-white border border-oth-line/70 rounded-xl px-3 py-2 font-body text-sm focus:outline-none focus:border-oth-blue" data-testid={`${testId}-input`} />
                <GhostBtn onClick={() => { if (draft.trim()) { onChange([...(items || []), draft.trim()]); setDraft(""); } }} testId={`${testId}-add`}>Add</GhostBtn>
            </div>
        </div>
    );
};

// ── Work-note tag editor (label + color) ────────────────────────────────────
const WorkTagEditor = ({ items, onChange, testId }) => {
    const [draft, setDraft] = useState("");
    const list = items || [];
    const addTag = () => {
        if (!draft.trim()) return;
        const color = WORK_TAG_PALETTE[list.length % WORK_TAG_PALETTE.length];
        onChange([...list, { label: draft.trim(), color }]);
        setDraft("");
    };
    const upd = (i, patch) => onChange(list.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
    const del = (i) => onChange(list.filter((_, idx) => idx !== i));
    return (
        <div data-testid={testId}>
            <div className="text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-2">Work note tags <span className="normal-case tracking-normal text-oth-ink3/70">(used in Clients → Work)</span></div>
            <div className="space-y-2 mb-2">
                {list.map((t, i) => (
                    <div key={i} className="flex items-center gap-2 flex-wrap" data-testid={`${testId}-row-${i}`}>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-body border shrink-0 ${workTagClasses(t.color)}`}>{t.label || "—"}</span>
                        <input value={t.label} onChange={(e) => upd(i, { label: e.target.value })} className="flex-1 min-w-[120px] bg-white border border-oth-line/70 rounded-xl px-3 py-2 font-body text-sm focus:outline-none focus:border-oth-blue" />
                        <div className="flex items-center gap-1">
                            {WORK_TAG_PALETTE.map((c) => (
                                <button key={c} type="button" onClick={() => upd(i, { color: c })} title={c} className={`w-6 h-6 rounded-full border ${workTagClasses(c)} ${t.color === c ? "ring-2 ring-offset-1 ring-[#0A0A0A]" : ""}`} data-testid={`${testId}-color-${i}-${c}`} />
                            ))}
                        </div>
                        <button onClick={() => del(i)} className="text-oth-ink3 hover:text-red-600" data-testid={`${testId}-del-${i}`}><Trash2 size={15} /></button>
                    </div>
                ))}
            </div>
            <div className="flex gap-2">
                <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} placeholder="New tag, press Enter" className="flex-1 bg-white border border-oth-line/70 rounded-xl px-3 py-2 font-body text-sm focus:outline-none focus:border-oth-blue" data-testid={`${testId}-input`} />
                <GhostBtn onClick={addTag} testId={`${testId}-add`}>Add</GhostBtn>
            </div>
        </div>
    );
};

export const SettingsModal = ({ onClose, config, onSave }) => {
    const [cfg, setCfg] = useState(() => config || {});
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

    const addColumn = () => set("lead_columns", [...(cfg.lead_columns || []), { key: `col_${Date.now()}`, label: "New column", type: "text" }]);
    const updColumn = (i, patch) => set("lead_columns", (cfg.lead_columns || []).map((c, idx) => idx === i ? { ...c, ...patch } : c));
    const delColumn = (i) => set("lead_columns", (cfg.lead_columns || []).filter((_, idx) => idx !== i));

    const save = async () => {
        setSaving(true);
        try { await onSave(cfg); toast.success("Settings saved."); onClose(); }
        catch { toast.error("Could not save settings."); }
        finally { setSaving(false); }
    };

    return (
        <Modal open onClose={onClose} title="CRM settings" subtitle="Columns, sources, tags, next steps & categories" maxWidth="max-w-2xl" testId="settings-modal">
            <div className="space-y-6">
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body">Custom lead columns</div>
                        <GhostBtn onClick={addColumn} testId="settings-add-column">+ Add column</GhostBtn>
                    </div>
                    <div className="space-y-2">
                        {(cfg.lead_columns || []).map((col, i) => (
                            <div key={col.key} className="grid grid-cols-12 gap-2 items-center" data-testid={`settings-column-${i}`}>
                                <input value={col.label} onChange={(e) => updColumn(i, { label: e.target.value })} className="col-span-6 bg-white border border-oth-line/70 rounded-xl px-3 py-2 font-body text-sm focus:outline-none focus:border-oth-blue" placeholder="Label" />
                                <select value={col.type} onChange={(e) => updColumn(i, { type: e.target.value })} className="col-span-5 bg-white border border-oth-line/70 rounded-xl px-3 py-2 font-body text-sm focus:outline-none focus:border-oth-blue">
                                    <option value="text">Text</option><option value="number">Number</option><option value="date">Date</option>
                                </select>
                                <button onClick={() => delColumn(i)} className="col-span-1 text-oth-ink3 hover:text-red-600 flex justify-center" data-testid={`settings-column-del-${i}`}><Trash2 size={15} /></button>
                            </div>
                        ))}
                        {(cfg.lead_columns || []).length === 0 && <div className="text-xs italic text-oth-ink3 font-body">No custom columns. Add ones like “AUM”, “City”, etc.</div>}
                    </div>
                </div>
                <ListEditor label="Lead sources" items={cfg.sources} onChange={(v) => set("sources", v)} testId="settings-sources" />
                <ListEditor label="Tags" items={cfg.tags} onChange={(v) => set("tags", v)} testId="settings-tags" />
                <ListEditor label="Next steps" items={cfg.next_steps} onChange={(v) => set("next_steps", v)} testId="settings-nextsteps" />
                <ListEditor label="Client categories" items={cfg.client_categories} onChange={(v) => set("client_categories", v)} testId="settings-categories" />
                <WorkTagEditor items={cfg.work_tags} onChange={(v) => set("work_tags", v)} testId="settings-worktags" />
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
                <GhostBtn onClick={onClose} testId="settings-cancel">Cancel</GhostBtn>
                <PrimaryBtn onClick={save} disabled={saving} testId="settings-save">{saving ? "Saving…" : "Save settings"}</PrimaryBtn>
            </div>
        </Modal>
    );
};

export { Tag };
